import { ArrowLeftIcon, ArrowRightIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Select,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import FormInput from "../../../../../../components/input";
import StepHeader from "../../../../../../components/StepperHeader";
import { problemStatement } from "../forms";
import { useFormik } from "formik";
import { Option } from "../../../../../../utils/types/props.dropdown";
import { color } from "../../../../../../utils/theme";
import Textarea from "../../../../../../components/textarea";
import { RootState } from "../../../../../../redux/reducer/rootReducer";
import { useDispatch, useSelector } from "react-redux";
import {
  getFormData,
  saveFormData,
} from "../../../../../../redux/slice/formSlice";

interface ProjectInformationProps {
  onNext: () => void;
  onPrev: () => void;
  onSave: () => void;

  loading: boolean;
}

const ProblemStatement = (props: ProjectInformationProps) => {
  const dispatch = useDispatch();
  const [showSummarizedProblemStatement, setShowSummarizedProblemStatement] =
    useState(false);

  const stepData = useSelector((state: RootState) => getFormData(state, "2"));
  const [saveAndExist, setSaveAndExist] = useState(false);

  const formik = useFormik({
    ...problemStatement,
    initialValues: stepData ? stepData : problemStatement.initialValues,
    onSubmit: (values) => {
      dispatch(saveFormData({ step: "2", data: values }));
      if (!saveAndExist) {
        setShowSummarizedProblemStatement(false);
        props.onNext();
      } else {
        setTimeout(() => {
          if (props.onSave) props.onSave();
        }, 2000);
      }
      // }
    },
  });

  useEffect(() => {}, [showSummarizedProblemStatement]);

  const [unitOfMeasureOptions] = useState<Option[]>([
    { value: "percentage", label: "Percentage" },
    { value: "ratio", label: "Ration" },
    { value: "count", label: "Count" },
  ]);

  const [selectedUnitOfMeasure, setSelectedUnitOfMeasure] = useState(
    stepData ? stepData.unitOfMeasure : ""
  );

  const [selectedTemplate, setSelectedTemplate] = useState(
    stepData ? stepData.problemStatement : ""
  );

  const replacePlaceholders = (template: string) => {
    return template
      .replace("(duration)", `${formik.values.from} to ${formik.values.to}`)
      .replace("(problem)", formik.values.problem || "")
      .replace("(magnitude)", formik.values.magnitude || "")
      .replace(
        "(department/unit name)",
        formik.values.whereDoesTheProblemExist || ""
      )
      .replace("(facility name)", user.user?.facility.name || "")
      .replace("(effects)", formik.values.effect || "");
  };
  const templates = [
    `Over the past (duration), (problem) has been observed. The magnitude of the problem was (magnitude) at (department/unit name) in (facility name), which might have resulted in (effects) the dash should use my form fields `,
    `Over the last (duration), a significant issue regarding (problem) has been observed. 
The scale of the issue is (magnitude), primarily affecting the (department/unit name) within (facility name). 
This could potentially lead to (effects) unless addressed promptly.`,
    `In the past (duration), inefficiencies in (problem) have been identified. 
These inefficiencies have reached a level of (magnitude) in (department/unit name) at (facility name), 
potentially causing (effects) and compromising overall service quality.`,
    `During (duration), operational risks associated with (problem) were noted. 
The extent of these risks is measured at (magnitude), particularly within the (department/unit name) of (facility name). 
This has implications for (effects) and requires immediate mitigation strategies.`,
    `Over the course of (duration), a persistent challenge in (problem) has been recorded. 
The magnitude of this challenge stands at (magnitude) in (department/unit name) at (facility name). 
Left unresolved, this could result in (effects), impacting key quality control metrics.`,
  ];
  const user = useSelector((state: RootState) => state.user);

  return (
    <VStack h="100%" w="100%" spacing={4}>
      {/* Step Header */}
      <StepHeader
        title="Step 1: Problem Statement"
        description="Provide essential details about the project and team members."
      />

      {/* Form Section */}
      <Box w="full" h={"full"} p={4} flex={1}>
        {showSummarizedProblemStatement ? (
          <HStack
            spacing={4}
            p={4}
            display={"flex"}
            justifyContent={"flex-start"}
            alignContent={"flex-start"}
            alignItems={"flex-start"}
            height={"90%"}
          >
            {/* Templates Section */}
            <Box
              w="40%"
              id="templates"
              h={"100%"}
              p={4}
              borderWidth="1px"
              borderRadius="md"
              bg="gray.100"
              overflowY="auto"
            >
              <Text color={color.primary} my={3}>
                Select Template for creating the problem statement
              </Text>
              <VStack spacing={3} h={"100%"}>
                {templates.map((template, index) => (
                  <Box
                    key={index}
                    w="100%"
                    h={"100%"}
                    p={3}
                    bg="white"
                    borderWidth="1px"
                    borderRadius="md"
                    boxShadow="md"
                    transition="all 0.3s"
                    _hover={{
                      bg: "blue.50",
                      transform: "scale(1.04)",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setSelectedTemplate(replacePlaceholders(template));
                      formik.setFieldValue(
                        "problemStatement",
                        replacePlaceholders(template)
                      );
                    }}
                  >
                    <Text fontSize="sm" textAlign={"center"} color="gray.600">
                      {template}
                    </Text>
                  </Box>
                ))}
              </VStack>
            </Box>

            {/* Content Section */}
            <Box
              w="60%"
              id="content"
              h={"100%"}
              p={4}
              borderWidth="1px"
              borderRadius="md"
              bg="gray.50"
            >
              <Text
                fontWeight="bold"
                fontFamily={"sans-serif"}
                color={color.primary}
                fontSize={27}
                textDecoration={"underline"}
              >
                Generated Problem Statement
              </Text>
              <Text
                mt={2}
                fontSize={"x-large"}
                fontWeight={"500"}
                fontFamily={"sans-serif"}
                color={color.text}
              >
                {selectedTemplate || "Click a template to preview it here!"}
              </Text>
            </Box>
          </HStack>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Flex direction="column" gap={6}>
              {/* Project Information */}
              <FormInput
                name="problem"
                isRequired={true}
                value={formik.values.problem}
                label="What is the problem?"
                type="text"
                id="problem"
                errorMessage={formik.errors.problem as string}
                onChange={(event) => {
                  formik.setFieldValue("problem", event.target.value);
                }}
              />

              {/* Dates */}
              <HStack spacing={4}>
                <FormInput
                  name="magnitude"
                  isRequired={true}
                  value={formik.values.magnitude}
                  label="Magnitude of the problem?"
                  type="text"
                  id="magnitude"
                  errorMessage={formik.errors.magnitude as string}
                  onChange={(event) => {
                    formik.setFieldValue("magnitude", event.target.value);
                  }}
                  onBlur={formik.handleBlur}
                />
                <FormControl style={{ width: "100%" }}>
                  <FormLabel
                    display={"flex"}
                    mb={0}
                    fontSize={18}
                    fontWeight={"500"}
                    color={color.text}
                  >
                    Unit Of Measure
                    <Text color={"red"} mx={1} fontSize={24}>
                      *
                    </Text>
                  </FormLabel>

                  <Select
                    placeholder=" "
                    value={selectedUnitOfMeasure}
                    onChange={(event) => {
                      setSelectedUnitOfMeasure(event.target.value);
                      formik.setFieldValue("unitOfMeasure", event.target.value);
                    }}
                  >
                    {unitOfMeasureOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  <Text
                    marginY={3}
                    fontWeight={"normal"}
                    color={"red"}
                    fontSize={13}
                    textAlign={"left"}
                  >
                    {typeof formik.errors.unitOfMeasure === "string" &&
                      formik.errors.unitOfMeasure}
                  </Text>
                </FormControl>
              </HStack>
              <FormInput
                name="whereDoesTheProblemExist"
                isRequired={true}
                value={formik.values.whereDoesTheProblemExist}
                label="Where does the problem exist? (Name of department/unit)"
                type="text"
                id="whereDoesTheProblemExist"
                errorMessage={formik.errors.whereDoesTheProblemExist as string}
                onChange={(event) => {
                  formik.setFieldValue(
                    "whereDoesTheProblemExist",
                    event.target.value
                  );
                }}
              />

              <HStack
                spacing={4}
                display={"flex"}
                alignContent={"center"}
                alignItems={"center"}
                flexDirection={"row"}
                justifyContent={"center"}
              >
                <FormLabel
                  display={"flex"}
                  flex={0.3}
                  mt={5}
                  fontWeight={"500"}
                  color={"black"}
                >
                  Duration of problem observed/documented
                  <Text color={"red"} mx={1} fontSize={24}>
                    *
                  </Text>
                </FormLabel>
                <HStack flex={0.7} spacing={4} display={"flex"}>
                  <FormInput
                    width={"30%"}
                    name="from"
                    isRequired={true}
                    value={formik.values.from}
                    label="From"
                    type="date"
                    id="from"
                    errorMessage={formik.errors.from as string}
                    onChange={(event) => {
                      formik.setFieldValue("from", event.target.value);
                    }}
                    onBlur={formik.handleBlur}
                  />
                  <FormInput
                    width={"30%"}
                    name="to"
                    isRequired={true}
                    value={formik.values.to}
                    label="To"
                    type="date"
                    id="to"
                    errorMessage={formik.errors.to as string}
                    onChange={(event) => {
                      formik.setFieldValue("to", event.target.value);
                    }}
                    onBlur={formik.handleBlur}
                  />
                </HStack>
              </HStack>
              <HStack
                display={"flex"}
                alignContent={"flex-start"}
                justifyContent={"flex-start"}
                flexDirection={"row"}
              >
                <FormLabel
                  display={"flex"}
                  flex={0.3}
                  mt={5}
                  fontWeight={"500"}
                  alignSelf={"flex-start"}
                  color={"black"}
                >
                  Effect caused by this problem
                  <Text color={"red"} mx={1} fontSize={24}>
                    *
                  </Text>
                </FormLabel>
                <Box flex={0.7}>
                  <Textarea
                    name="effect"
                    isRequired={false}
                    multiple={true}
                    value={formik.values.effect}
                    label=""
                    type="text"
                    id="effect"
                    errorMessage={formik.errors.effect as string}
                    onChange={(event) => {
                      formik.setFieldValue("effect", event.target.value);
                    }}
                    onBlur={formik.handleBlur}
                  />
                </Box>
              </HStack>
            </Flex>
          </form>
        )}
      </Box>
      <HStack
        flex={0.1}
        w="100%"
        justifyContent="space-between"
        p={4}
        bg="gray.50"
      >
        <Button
          colorScheme="blue"
          variant="outline"
          leftIcon={<ArrowLeftIcon />}
          onClick={() => {
            showSummarizedProblemStatement
              ? setShowSummarizedProblemStatement(false)
              : props.onPrev && props.onPrev();
          }}
        >
          Previous
        </Button>
        <HStack>
          <Button
            isLoading={props.loading}
            isDisabled={!formik.isValid}
            colorScheme="blue"
            variant="outline"
            onClick={() => {
              setSaveAndExist(true);
              formik.handleSubmit();
              console.log("Saved values:", formik.values);
            }}
          >
            Save & Exit
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={!formik.isValid}
            onClick={() => {
              setSaveAndExist(false);
              if (formik.isValid && formik.values.problemStatement === "") {
                setShowSummarizedProblemStatement(true);
              } else if (
                showSummarizedProblemStatement &&
                formik.values.problemStatement !== ""
              ) {
                formik.handleSubmit();
              } else {
                setShowSummarizedProblemStatement(true);
              }
              // formik.handleSubmit();
            }}
            w={"50%"}
            rightIcon={<ArrowRightIcon />}
          >
            Next
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );
};

export default ProblemStatement;
